import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { ShieldCheck, Lock, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errCode, errMsg } from "@/lib/api";

function Codes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="space-y-4" data-testid="admin-recovery-codes">
      <p className="text-sm">Save these one-time recovery codes somewhere safe (password manager). Each works once if you lose your phone. They won’t be shown again.</p>
      <ul className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary rounded-lg p-4">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
      <Button className="w-full h-12" onClick={onDone} data-testid="admin-recovery-codes-done">I’ve saved my codes</Button>
    </div>
  );
}

async function copyText(text: string, el: HTMLElement | null) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    if (!el) return false;
    const r = document.createRange(); r.selectNodeContents(el);
    const sel = window.getSelection(); sel?.removeAllRanges(); sel?.addRange(r);
    return document.execCommand("copy");
  }
}

function SetupKey({ secret, email }: { secret: string; email: string }) {
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const copy = async () => {
    if (await copyText(secret, ref.current)) { setCopied(true); toast.success("Setup key copied"); setTimeout(() => setCopied(false), 2500); }
    else toast.error("Couldn’t copy — press and hold the key to select it");
  };
  return (
    <div className="rounded-lg border bg-secondary p-4 space-y-3" data-testid="admin-mfa-manual">
      <label className="text-sm font-semibold block" htmlFor="mfa-setup-key">Setup key (letters and numbers)</label>
      <span id="mfa-setup-key" ref={ref} className="block font-mono text-base tracking-wider select-all bg-card border rounded-md p-3" data-testid="admin-mfa-secret">
        {secret.match(/.{1,4}/g)?.map((g, i) => <span key={i} className="inline-block whitespace-nowrap mr-2">{g}</span>)}
      </span>
      <Button type="button" variant="outline" className="w-full h-12" onClick={copy} data-testid="admin-mfa-copy-secret">
        {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}{copied ? "Copied" : "Copy setup key"}
      </Button>
      <dl className="text-xs text-muted-foreground grid grid-cols-[auto_1fr] gap-x-3 gap-y-1" data-testid="admin-mfa-manual-details">
        <dt>Account</dt><dd className="break-all">{email}</dd>
        <dt>Issuer</dt><dd>Instant Quote Wizard</dd>
        <dt>Type</dt><dd>Time-based, 6 digits, every 30 seconds</dd>
      </dl>
      <p className="text-xs text-muted-foreground">Spaces are only for readability — the copied key has none. Paste it in your app’s “Enter a setup key” option.</p>
    </div>
  );
}

function Setup({ onEnabled }: { onEnabled: (codes: string[]) => void }) {
  const [s, setS] = useState<any>(null);
  const [code, setCode] = useState("");
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api.post("/admin/mfa/setup").then((r) => setS(r.data)).catch((e) => { if (errCode(e) !== "ALREADY_ENROLLED") toast.error(errMsg(e)); });
  }, []);
  const enable = async () => {
    try { const { data } = await api.post("/admin/mfa/enable", { code }); onEnabled(data.recovery_codes); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return <p className="text-sm text-muted-foreground">Preparing…</p>;
  return (
    <div className="space-y-4" data-testid="admin-mfa-setup">
      <p className="text-sm">Two-step verification is required for Super Admin. Add this account to an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Authy, or iPhone Passwords), then enter the 6-digit code it shows.</p>
      <div className="space-y-2">
        <p className="text-sm font-semibold">Setting up on this phone?</p>
        <a href={s.otpauth_uri} className="w-full h-12 inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-medium" data-testid="admin-mfa-open-app">
          <ExternalLink className="h-4 w-4 mr-2" />Open in authenticator app
        </a>
        <p className="text-xs text-muted-foreground">If nothing opens, use the setup key below instead.</p>
      </div>
      <SetupKey secret={s.secret} email={s.account || ""} />
      <details className="rounded-lg border p-3" data-testid="admin-mfa-qr-toggle">
        <summary className="text-sm font-semibold cursor-pointer min-h-[32px]">Using a second device? Show QR code</summary>
        <div className="bg-white rounded-lg p-2 w-fit mx-auto mt-3 [&_svg]:w-48 [&_svg]:h-48" dangerouslySetInnerHTML={{ __html: s.qr_svg }} data-testid="admin-mfa-qr" />
      </details>
      <label className="text-sm font-semibold block" htmlFor="mfa-setup-code">6-digit verification code (numbers only)</label>
      <Input id="mfa-setup-code" className="h-12 text-lg font-mono tracking-widest" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6} value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" data-testid="admin-mfa-setup-code" />
      <Button className="w-full h-12" disabled={code.length !== 6} onClick={enable} data-testid="admin-mfa-enable">Turn on two-step verification</Button>
    </div>
  );
}

function Verify({ onOk }: { onOk: () => void }) {
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const go = async () => {
    try { await api.post("/admin/mfa/verify", recovery ? { recovery_code: code } : { code }); onOk(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-mfa-verify">
      <p className="text-sm">{recovery ? "Enter one of your recovery codes." : "Enter the 6-digit code from your authenticator app."}</p>
      <Input className="h-12 text-lg font-mono tracking-widest" inputMode={recovery ? "text" : "numeric"} pattern={recovery ? undefined : "[0-9]*"} autoComplete={recovery ? "off" : "one-time-code"}
        autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={recovery ? 20 : 6} value={code}
        onChange={(e) => setCode(recovery ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="admin-mfa-code" />
      <Button className="w-full h-12" disabled={recovery ? code.length < 6 : code.length !== 6} onClick={go} data-testid="admin-mfa-verify-button">Verify</Button>
      <button className="text-xs underline text-muted-foreground min-h-[44px]" onClick={() => { setRecovery(!recovery); setCode(""); }} data-testid="admin-mfa-toggle-recovery">
        {recovery ? "Use authenticator code instead" : "Lost your phone? Use a recovery code"}
      </button>
    </div>
  );
}

const TABS = [["/app/admin", "Overview"], ["/app/admin/companies", "Companies"], ["/app/admin/settings", "Costs & security"], ["/app/admin/audit", "Audit log"]];

export default function AdminGate() {
  const [st, setSt] = useState<any>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const load = () => api.get("/admin/mfa/status").then((r) => setSt(r.data)).catch(() => setSt({ forbidden: true }));
  useEffect(() => { load(); }, []);
  const lock = async () => { await api.post("/admin/mfa/lock"); load(); };
  if (!st) return <p className="text-muted-foreground">Loading…</p>;
  if (st.forbidden) return <p data-testid="admin-forbidden">Not authorized.</p>;
  if (codes) return <div className="max-w-md mx-auto bg-card border rounded-xl p-6"><Codes codes={codes} onDone={async () => { await load(); setCodes(null); }} /></div>;
  if (!st.verified) return (
    <div className="max-w-md mx-auto bg-card border rounded-xl p-6 space-y-4 fade-up">
      <h1 className="text-xl font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" />Super Admin verification</h1>
      {st.enrolled ? <Verify onOk={load} /> : <Setup onEnabled={setCodes} />}
    </div>
  );
  return (
    <div className="fade-up" data-testid="super-admin">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">Super Admin</h1>
        <Button variant="outline" size="sm" onClick={lock} data-testid="admin-lock-button"><Lock className="h-4 w-4 mr-1" />Lock</Button>
      </div>
      <nav className="flex gap-2 overflow-x-auto pb-2 mb-5 -mx-1 px-1" aria-label="Admin sections">
        {TABS.map(([to, label]) => (
          <NavLink key={to} to={to} end className={({ isActive }) => `whitespace-nowrap h-10 px-4 inline-flex items-center rounded-full text-sm border ${isActive ? "bg-foreground text-background" : "bg-card"}`}
            data-testid={`admin-tab-${label.split(" ")[0].toLowerCase()}`}>{label}</NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
